import { api } from '../lib/api';
import type {
  CompleteVideoResponse,
  CompletedPart,
  CreateVideoBody,
  CreateVideoResponse,
  MorePartsResponse,
  UploadPart,
  VideoDetail,
  VideoListResponse,
  VideoStatus,
  Visibility,
} from '../lib/types';
import type { UploadTransport } from '../recorder/uploader';

export type LibrarySort = 'newest' | 'oldest' | 'views';

export interface LibraryQuery {
  /** Only what the person recorded, rather than everything in their organization too. */
  mine?: boolean;
  limit?: number;
  cursor?: string | null;
  /** Titles and transcripts. */
  q?: string;
  /** Recorded on or after, ISO. */
  since?: string | null;
  until?: string | null;
  sort?: LibrarySort;
  archived?: boolean;
}

export function libraryScopeKey(query: LibraryQuery): string {
  return [
    query.mine ? 'mine' : 'all',
    query.q ?? '',
    query.since ?? '',
    query.until ?? '',
    query.sort ?? 'newest',
    query.archived ? 'archived' : 'live',
  ].join(':');
}

function libraryPath(query: LibraryQuery): string {
  const params = new URLSearchParams();
  if (query.mine) params.set('mine', '1');
  if (query.limit) params.set('limit', String(query.limit));
  if (query.cursor) params.set('cursor', query.cursor);
  if (query.q) params.set('q', query.q);
  if (query.since) params.set('since', query.since);
  if (query.until) params.set('until', query.until);
  if (query.sort && query.sort !== 'newest') params.set('sort', query.sort);
  if (query.archived) params.set('archived', '1');
  const search = params.toString();
  return `/v1/videos${search ? `?${search}` : ''}`;
}

export function fetchLibrary(query: LibraryQuery): Promise<VideoListResponse> {
  return api<VideoListResponse>(libraryPath(query));
}

export function fetchVideo(id: string): Promise<VideoDetail> {
  return api<VideoDetail>(`/v1/videos/${id}`);
}

export function updateVideo(
  id: string,
  body: { title?: string; summary?: string | null; hiddenLinks?: string[]; visibility?: Visibility },
): Promise<VideoDetail> {
  return api<VideoDetail>(`/v1/videos/${id}`, { method: 'PATCH', body });
}

export function deleteVideo(id: string): Promise<void> {
  return api<void>(`/v1/videos/${id}`, { method: 'DELETE' });
}

export function restoreVideo(id: string): Promise<{ ok: boolean }> {
  return api(`/v1/videos/${id}/restore`, { method: 'POST' });
}

/** Copy for a status badge. Written to be read by a person, not a machine. */
export function statusLabel(status: VideoStatus): string {
  switch (status) {
    case 'uploading':
      return 'Uploading';
    case 'processing':
      return 'Processing';
    case 'ready':
      return 'Ready';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
}

/**
 * The recorder's upload adapter.
 *
 * Pass a `requestToken` on /r/:token so every call carries the anonymous
 * session plus the link token, which is what makes slate-api file the upload
 * in the requester's library instead of rejecting it.
 */
export function createUploadTransport(requestToken?: string): UploadTransport {
  const extra = requestToken ? { requestToken, auth: 'optional' as const } : {};

  return {
    createVideo(body: CreateVideoBody): Promise<CreateVideoResponse> {
      return api<CreateVideoResponse>('/v1/videos', { method: 'POST', body, ...extra });
    },
    async moreParts(videoId: string, fromPartNumber: number, count: number): Promise<UploadPart[]> {
      const response = await api<MorePartsResponse>(`/v1/videos/${videoId}/parts`, {
        method: 'POST',
        body: { fromPartNumber, count },
        ...extra,
      });
      return response.parts;
    },
    complete(
      videoId: string,
      body: { parts: CompletedPart[]; durationSec?: number; title?: string },
    ): Promise<CompleteVideoResponse> {
      return api<CompleteVideoResponse>(`/v1/videos/${videoId}/complete`, {
        method: 'POST',
        body,
        ...extra,
      });
    },
    discard(videoId: string): Promise<void> {
      return api<void>(`/v1/videos/${videoId}`, { method: 'DELETE', ...extra });
    },
  };
}
